import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, TruckIcon } from 'lucide-react';

const CartPage = ({ cart, onUpdateQuantity, onRemoveFromCart }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const subtotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = subtotal > 50 ? 0 : 4.99;
  const total = subtotal + shipping;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-center">
              <div className="text-gray-400 mb-6">
                <ShoppingCart className="h-24 w-24 mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('cart.empty_cart')}</h2>
              <p className="text-gray-600 mb-6">
                {t('cart.empty_cart_sub')}
              </p>
              <Button
                onClick={() => navigate('/products')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                {t('products.browse_products')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/products')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('cart.continue_shopping')}
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{t('cart.shopping_cart')}</h1>
          <p className="text-gray-600 mt-2">{t('cart.items_count', { count: cart.length })}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {cart.map((item, index) => (
                  <div key={index}>
                    <div className="p-6">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div
                          className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                          onClick={() => navigate(`/product/${item.product.id}`)}
                        >
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-sm font-semibold text-blue-600 mb-1">
                                {item.product.brand}
                              </p>
                              <h3
                                className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() => navigate(`/product/${item.product.id}`)}
                              >
                                {item.product.name}
                              </h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {t('products.sku')}: {item.product.partNumber}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onRemoveFromCart(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="flex items-center justify-between mt-4">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center font-semibold">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>

                            {/* Price */}
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900">
                                OMR {(item.product.price * item.quantity).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-600">
                                OMR {item.product.price.toFixed(2)} each
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < cart.length - 1 && <Separator />}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">{t('cart.order_summary')}</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-700">
                    <span>{t('cart.subtotal')}</span>
                    <span className="font-semibold">OMR {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>{t('cart.shipping')}</span>
                    <span className="font-semibold">
                      {shipping === 0 ? t('cart.free') : `OMR ${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {subtotal < 50 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <TruckIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                        <p className="text-sm text-blue-900">
                          {t('cart.free_delivery_threshold', { amount: (50 - subtotal).toFixed(2) })}
                        </p>
                      </div>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold text-gray-900">
                    <span>{t('cart.total')}</span>
                    <span>OMR {total.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 mb-4"
                  onClick={() => navigate('/checkout')}
                >
                  {t('cart.checkout')}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/products')}
                >
                  {t('cart.continue_shopping')}
                </Button>

                {/* Discount Code */}
                <div className="mt-6 pt-6 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cart.have_discount_code')}
                  </label>
                  <div className="flex gap-2">
                    <Input placeholder={t('cart.enter_code')} dir={i18n.language === 'ar' ? 'ltr' : 'ltr'} />
                    <Button variant="outline">{t('cart.apply')}</Button>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t space-y-3">
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="bg-green-100 p-2 rounded">
                      <TruckIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span>{t('home.free_delivery')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="bg-blue-100 p-2 rounded">
                      <ShoppingCart className="h-4 w-4 text-blue-600" />
                    </div>
                    <span>Easy 30-day returns</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
